import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getData_Any2Column, formatRecipe, deleteData, getItemsbyType } from "../service/APIService";
import img3 from "../images/frying-pan.gif";
import Modal from "react-bootstrap/Modal";
import secureLocalStorage from "react-secure-storage";
import { useSubscription } from "../context/SubscriptionContext";

const RecipeHistoryComponent = ({ showLatest = false }) => {
  const { isSubscribed } = useSubscription();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [visibleRecipes, setVisibleRecipes] = useState(20);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchPublic, setSearchPublic] = useState(false);
  const [touchStartY, setTouchStartY] = useState(null);
  // Retrieve the logged-in user's email from localStorage
  const loggedInUserEmail = JSON.parse(secureLocalStorage.getItem("loggedInUser"))?.email || "guest";

  useEffect(() => {
    const fetchRecipeHistory = async () => {
      try {
        setLoading(true);
        let data;
        if (searchPublic) {
          data = await getItemsbyType("recipe-ai");
        } else {
          data = await getData_Any2Column("email", loggedInUserEmail, "type", "recipe-ai");
        }

        // If `showLatest` is true, limit to the latest 6 recipes
        const sortedRecipes = data.sort((a, b) => new Date(b.date) - new Date(a.date));
        setRecipes(showLatest ? sortedRecipes.slice(0, 6) : sortedRecipes);

        setError(null);
      } catch (err) {
        setError("Failed to fetch recipe history. Please try again.");
        console.error("Error fetching recipe history:", err);
      } finally {
        setLoading(false);
      }
    };

    if (loggedInUserEmail !== "guest" || searchPublic) {
      fetchRecipeHistory();
    } else {
      setLoading(false);
    }
  }, [loggedInUserEmail, showLatest, searchPublic]);

  const handleShowDetails = (recipe) => {
    setSelectedRecipe(recipe);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };
  const handleLoadMore = () => {
    setVisibleRecipes((prev) => prev + 10); // Show 10 more records
  };

  const handleDeleteRecipe = async (recipeId) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this recipe?");
    if (!confirmDelete) return;

    try {
      await deleteData(recipeId); // Call the delete API
      setRecipes((prevRecipes) => prevRecipes.filter((recipe) => recipe.id !== recipeId)); // Remove from UI
    } catch (err) {
      console.error("Error deleting recipe:", err);
      setError("Failed to delete the recipe. Please try again.");
    }
  };
  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear().toString().slice(-2);
      let hours = date.getHours();
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12;
      hours = hours ? hours : 12;
      return `${month}/${day}/${year} ${hours}:${minutes} ${ampm}`;
    } catch (e) {
      return dateString;
    }
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      const now = new Date();
      const isToday =
        date.getDate() === now.getDate() &&
        date.getMonth() === now.getMonth() &&
        date.getFullYear() === now.getFullYear();

      let hours = date.getHours();
      const minutes = date.getMinutes().toString().padStart(2, "0");
      const ampm = hours >= 12 ? "pm" : "am";
      hours = hours % 12;
      hours = hours ? hours : 12;
      const timeStr = `${hours}:${minutes} ${ampm}`;

      if (isToday) return timeStr;

      const yesterday = new Date(now);
      yesterday.setDate(now.getDate() - 1);
      const isYesterday =
        date.getDate() === yesterday.getDate() &&
        date.getMonth() === yesterday.getMonth() &&
        date.getFullYear() === yesterday.getFullYear();

      if (isYesterday) return `Yesterday ${timeStr}`;

      const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      return `${months[date.getMonth()]} ${date.getDate()}, ${timeStr}`;
    } catch (e) {
      return dateString;
    }
  };

  const filteredRecipes = recipes.filter((recipe) => (recipe.title || "").toLowerCase().includes(searchQuery.toLowerCase()));
  const recipesToDisplay = showLatest ? filteredRecipes.slice(0, 6) : filteredRecipes.slice(0, visibleRecipes);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mt-2 mb-3">
        <div className="d-flex align-items-center gap-2">
          <h6 className="fw-bold text-dark mb-0">{showLatest ? "Recent Recipes" : "Recipe AI History"}</h6>
          {showLatest && (
            <span className="badge rounded-pill bg-success bg-opacity-10 text-success border border-success border-opacity-25" style={{ fontSize: "10px" }}>
              Latest 6
            </span>
          )}
        </div>
        {showLatest ? (
          <Link to="/history" className="text-decoration-none fw-semibold text-success small d-flex align-items-center gap-1" style={{ fontSize: "12px" }}>
            <span>View All</span>
            <i className="fi fi-rr-arrow-right" style={{ fontSize: "10px" }}></i>
          </Link>
        ) : (
          isSubscribed && (
            <div className="d-flex gap-2">
              <span className="badge bg-light text-dark border" style={{ fontSize: "0.65rem" }}>
                Mine: {recipes.filter(r => r.email === loggedInUserEmail).length}
              </span>
              <span className="badge border" style={{
                fontSize: "0.65rem",
                backgroundColor: "rgba(67, 134, 170, 0.1)",
                color: "#4386aa",
                borderColor: "rgba(67, 134, 170, 0.2) !important"
              }}>
                Public: {recipes.filter(r => r.email !== loggedInUserEmail).length}
              </span>
            </div>
          )
        )}
      </div>
      {!showLatest && (
        <div className="mb-3">
          <input
            type="text"
            className="form-control mb-2"
            placeholder="Search by recipe title..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              borderRadius: "9999px",
              padding: "10px 18px",
              border: "1.5px solid #e2e8f0",
              fontSize: "0.88rem"
            }}
          />
          <div className="form-check form-switch mt-2">
            <input
              className="form-check-input"
              type="checkbox"
              id="publicSearchToggle"
              checked={searchPublic}
              disabled={!isSubscribed}
              onChange={(e) => {
                setSearchPublic(e.target.checked);
                setVisibleRecipes(20); // Reset pagination
              }}
            />
            <label
              className={`form-check-label ${!isSubscribed ? "text-muted" : "text-dark"}`}
              htmlFor="publicSearchToggle"
              style={{ fontSize: "0.85rem", opacity: !isSubscribed ? 0.6 : 1 }}
            >
              Search Public Recipes {!isSubscribed && (
                <Link to="/upgrade" className="text-dark text-decoration-none ms-1" style={{ fontSize: "0.75rem" }}>
                  (Upgrade to Pro to unlock)
                </Link>
              )}
            </label>
          </div>
        </div>
      )}
      {loading ? (
        <div className="text-center py-4">
          <div className="d-inline-flex align-items-center justify-content-center p-3 rounded-circle" style={{ background: "rgba(34, 197, 94, 0.1)" }}>
            <span style={{ fontSize: "2.4rem", animation: "potSizzle 1.2s ease-in-out infinite alternate", display: "inline-block" }}>🍳</span>
          </div>
          <p className="text-secondary small mt-2 mb-0 fw-medium">Loading recipes...</p>
        </div>
      ) : error ? (
        <p style={{ color: "red" }}>{error}</p>
      ) : recipes.length === 0 ? (
        <p className="text-muted text-center py-4">No recipes found.</p>
      ) : (
        <div className="recipe-history-list">
          <div className="d-flex flex-column gap-2 mb-2">
            {recipesToDisplay.map((recipe) => (
              <div
                key={recipe.id}
                className="recent-food-horizontal-card"
                onClick={() => handleShowDetails(recipe)}
                role="button"
                tabIndex={0}
              >
                {/* Left Thumbnail (64x64 compact mobile-first) */}
                <div
                  className="food-card-thumb"
                  style={{
                    width: "64px",
                    height: "64px",
                    minWidth: "64px",
                    maxWidth: "64px",
                    borderRadius: "14px",
                    overflow: "hidden",
                    flexShrink: 0
                  }}
                >
                  {recipe.image ? (
                    <img
                      src={recipe.image}
                      alt={recipe.title}
                      className="food-card-img"
                      style={{
                        width: "100%",
                        height: "100%",
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "cover",
                        display: "block"
                      }}
                      loading="lazy"
                    />
                  ) : (
                    <div className="food-card-placeholder recipe">
                      <i className="fi fi-rr-restaurant"></i>
                    </div>
                  )}
                </div>

                {/* Center Content */}
                <div className="food-card-info">
                  {/* Top line: Name + Time */}
                  <div className="food-card-header">
                    <h6 className="food-card-name" title={recipe.title ? recipe.title.replace(/"/g, "").trim() : "Unnamed Recipe"}>
                      {recipe.title ? recipe.title.replace(/"/g, "").trim() : "Unnamed Recipe"}
                    </h6>
                    <span className="food-card-time">
                      {formatTimeAgo(recipe.date)}
                    </span>
                  </div>

                  {/* Middle line: Cuisine + AI Badge */}
                  <div className="food-card-subline">
                    {recipe.cuisine && (
                      <span className="food-cuisine-chip">
                        {recipe.cuisine}
                      </span>
                    )}
                    <span className="food-ai-chip">
                      ✨ AI Recipe
                    </span>
                    {recipe.email !== loggedInUserEmail && (
                      <span className="food-ai-chip" title="Public recipe">
                        <i className="fi fi-rr-globe me-1"></i> Public
                      </span>
                    )}
                  </div>

                  {/* Bottom line: Ingredients summary preview */}
                  <div className="food-preview-text">
                    <i className="fi fi-rr-leaf text-success" style={{ fontSize: "10px" }}></i>
                    <span>
                      {recipe.ingredients
                        ? recipe.ingredients.split(",").slice(0, 3).map(s => s.trim()).filter(Boolean).join(" • ")
                        : "Fresh ingredients & seasoning"}
                    </span>
                  </div>
                </div>

                {/* Right Action Chevron or Delete */}
                <div className="food-card-actions">
                  {isSubscribed && recipe.email === loggedInUserEmail && !showLatest ? (
                    <button
                      type="button"
                      className="food-card-delete-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteRecipe(recipe.id);
                      }}
                      title="Delete recipe"
                      aria-label="Delete recipe"
                    >
                      <i className="fi fi-rr-trash"></i>
                    </button>
                  ) : (
                    <div className="food-card-chevron">
                      <i className="fi fi-rr-angle-small-right"></i>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          {showLatest ? (
            <div align="center" className="mt-3 mb-1">
              <Link
                to="/history"
                className="btn py-2 px-4 rounded-pill fw-bold shadow-xs d-inline-flex align-items-center gap-2 text-decoration-none view-full-history-btn"
                style={{
                  fontSize: "0.82rem"
                }}
              >
                <span>View Full Recipe & Scan History</span>
                <i className="fi fi-rr-arrow-right" style={{ fontSize: "0.85rem" }}></i>
              </Link>
            </div>
          ) : (
            visibleRecipes < filteredRecipes.length && (
              <div align="center" className="mt-4 mb-2">
                <button
                  type="button"
                  className="btn py-2 px-5 rounded-pill fw-bold shadow-sm d-flex align-items-center gap-2"
                  style={{
                    backgroundColor: "#0f1f14",
                    color: "#ffffff",
                    border: "1px solid rgba(34, 197, 94, 0.35)",
                    boxShadow: "0 4px 14px rgba(15, 31, 20, 0.35)"
                  }}
                  onClick={handleLoadMore}
                >
                  <span>Load More</span>
                  <i className="fi fi-rr-angle-small-down" style={{ fontSize: "1.2rem" }}></i>
                </button>
              </div>
            )
          )}
        </div>
      )}

      <Modal
        show={showModal}
        onHide={handleCloseModal}
        onExited={() => setSelectedRecipe(null)}
        className="bottom-sheet-modal"
        dialogClassName="bottom-sheet-dialog"
      >
        <Modal.Body className="p-0">
          {selectedRecipe && (
            <div
              onTouchStart={(e) => setTouchStartY(e.touches[0].clientY)}
              onTouchEnd={(e) => {
                if (touchStartY !== null && e.changedTouches[0].clientY - touchStartY > 80) {
                  handleCloseModal();
                }
                setTouchStartY(null);
              }}
            >
              {/* Full-width Hero Image with close button overlay */}
              <div className="recipe-modal-hero">
                {selectedRecipe.image ? (
                  <img
                    src={selectedRecipe.image}
                    alt={selectedRecipe.title}
                    className="recipe-modal-hero-img"
                  />
                ) : (
                  <div className="recipe-modal-hero-placeholder">
                    <i className="fas fa-utensils"></i>
                  </div>
                )}
                {/* Close button overlaid on image */}
                <button
                  type="button"
                  className="recipe-modal-close-btn"
                  onClick={handleCloseModal}
                  aria-label="Close"
                >
                  <i className="fas fa-times"></i>
                </button>
                {/* Floating Cuisine Badge */}
                {selectedRecipe.cuisine && (
                  <span className="recipe-modal-cuisine-badge">
                    {selectedRecipe.cuisine}
                  </span>
                )}
              </div>

              {/* Content below image */}
              <div className="px-3 pt-3 pb-2">
                {/* Meta Header Row */}
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span className="text-muted d-flex align-items-center gap-1" style={{ fontSize: "0.74rem" }}>
                    <i className="fi fi-rr-clock"></i> {formatDate(selectedRecipe.date)}
                  </span>
                  <span className="badge rounded-pill" style={{ background: "#f0fdf4", color: "#16a34a", border: "1px solid #dcfce7", fontSize: "11px", fontWeight: "600" }}>
                    ✨ AI Chef
                  </span>
                </div>

                {/* Title */}
                <h4 className="fw-bolder recipe-modal-title mb-3" style={{ fontSize: "1.25rem", lineHeight: "1.3", letterSpacing: "-0.3px" }}>
                  {selectedRecipe.title?.replace(/"/g, "").trim()}
                </h4>

                {/* Ingredients Section */}
                {selectedRecipe.ingredients && (
                  <div className="recipe-modal-section mb-3">
                    <div className="recipe-modal-section-header">
                      <i className="fi fi-rr-restaurant"></i>
                      <span>Ingredients</span>
                    </div>
                    <div className="recipe-ingredients-pills">
                      {selectedRecipe.ingredients
                        .split(",")
                        .map((item) => item.trim())
                        .filter(Boolean)
                        .map((item, idx) => (
                          <span key={idx} className="recipe-ingredient-chip">
                            ✓ {item}
                          </span>
                        ))}
                    </div>
                  </div>
                )}

                {/* Instructions Section */}
                <div className="recipe-modal-section mb-2">
                  <div className="recipe-modal-section-header">
                    <i className="fi fi-rr-list-check"></i>
                    <span>Step-by-Step Instructions</span>
                  </div>
                  <div className="recipe-instructions-body">
                    {formatRecipe(selectedRecipe.recipeText)}
                  </div>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="border-0 pt-0 px-3 pb-3">
          <button
            className="btn w-100 py-3 rounded-pill fw-bold text-white shadow-none recipe-modal-bottom-btn"
            style={{
              background: "#0f1f14",
              border: "1px solid rgba(34, 197, 94, 0.35)",
              fontSize: "0.95rem",
              boxShadow: "0 4px 14px rgba(15, 31, 20, 0.35)"
            }}
            onClick={handleCloseModal}
          >
            Close Recipe
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default RecipeHistoryComponent;
